import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Shared wire schemas for tariff management endpoints (admin-arch §13 +
 * PRD §12.2).
 *
 * Catalog is a FIXED SET of three rows (Lite / Standard / Pro) seeded by
 * migration 0002. There is no POST / DELETE on the public surface — only
 * super-admins may PATCH catalog rows, and the `code` / `id` columns are
 * immutable identifiers.
 *
 * Currency: prices are stored and transmitted as bigint kopecks per ADR-010.
 * The wire uses `number` (JSON has no bigint) — kopecks comfortably fit in
 * `Number.MAX_SAFE_INTEGER`, and the BE column is `bigint mode: 'number'`.
 */
const PRICE_MAX_KOP = 10_000_000_000; // 100 млн рублей в копейках — defence-in-depth cap
const NAME_MAX = 64;
const DESCRIPTION_MAX = 2048;
const priceKopSchema = z
    .number()
    .int()
    .nonnegative()
    .max(PRICE_MAX_KOP, `Price must be at most ${PRICE_MAX_KOP} kopecks`);
/**
 * `limits` is an open jsonb bag — admin-arch §13 defines `track_limit`,
 * `device_limit`, `ai_quota_minutes` as known keys but leaves the schema
 * extensible. Each known key is validated; unknown keys are stripped by
 * `z.object(...).strict()` (PATCH refuses unknowns rather than silently
 * persisting them).
 */
export const tariffLimitsSchema = z
    .object({
    track_limit: z.number().int().nonnegative().max(1_000_000).optional(),
    device_limit: z.number().int().nonnegative().max(10_000).optional(),
    ai_quota_minutes: z.number().int().nonnegative().max(1_000_000).optional(),
})
    .strict();
export const tariffAdminSchema = z.object({
    id: uuidSchema,
    code: z.string().min(1).max(NAME_MAX),
    name: z.string().min(1).max(NAME_MAX),
    monthly_price_kop: priceKopSchema,
    connection_fee_kop: priceKopSchema,
    description: z.string().nullable(),
    limits: tariffLimitsSchema,
    is_active: z.boolean(),
    display_order: z.number().int().nullable(),
    default_preset_id: uuidSchema.nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
});
/**
 * `PATCH /admin/settings/tariffs/:id` payload. Whitelisted fields ONLY —
 * `id` and `code` are immutable (catalog identifier; downstream
 * subscriptions reference tariffs by code). Attempts to send them are
 * rejected at the zod boundary via `.strict()`.
 *
 * Behaviour contract (impl report): changing `monthly_price_kop` /
 * `connection_fee_kop` affects ONLY new subscriptions activated AFTER the
 * change. Existing subscriptions carry their `tariff_price` captured at
 * activation time — see `customers.subscriptions` schema.
 */
export const tariffPatchInputSchema = z
    .object({
    name: z.string().min(1).max(NAME_MAX).optional(),
    monthly_price_kop: priceKopSchema.optional(),
    connection_fee_kop: priceKopSchema.optional(),
    description: z.string().max(DESCRIPTION_MAX).nullable().optional(),
    limits: tariffLimitsSchema.optional(),
    is_active: z.boolean().optional(),
    display_order: z.number().int().nullable().optional(),
    default_preset_id: uuidSchema.nullable().optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided');
/**
 * `POST /admin/settings/tariffs` payload — Wave 2.64 BE (TASK-095a-followup).
 *
 * The catalog was a closed three-row set seeded by migration 0002 until
 * this surface landed; super-admins may now add extra tariffs (e.g. an
 * "Enterprise" tier) without a migration. `code` is mandatory and globally
 * UNIQUE — duplicate codes return 409 `TARIFF_CODE_DUPLICATE`.
 *
 * Same field whitelist as PATCH minus the partial-update semantics:
 * `name`, `code`, and `monthly_price_kop` are REQUIRED; the rest have
 * sensible defaults at the DB level.
 */
export const tariffCreateInputSchema = z
    .object({
    code: z
        .string()
        .trim()
        .min(1)
        .max(NAME_MAX)
        .regex(/^[a-z][a-z0-9_]*$/, 'Lowercase snake_case'),
    name: z.string().trim().min(1).max(NAME_MAX),
    monthly_price_kop: priceKopSchema,
    connection_fee_kop: priceKopSchema.optional(),
    description: z.string().max(DESCRIPTION_MAX).nullable().optional(),
    limits: tariffLimitsSchema.optional(),
    is_active: z.boolean().optional(),
    display_order: z.number().int().nullable().optional(),
    default_preset_id: uuidSchema.nullable().optional(),
})
    .strict();
/**
 * List response — fixed-set catalog (no pagination). FE renders all rows.
 */
export const tariffListResponseSchema = z.object({
    items: z.array(tariffAdminSchema),
});
//# sourceMappingURL=tariff.js.map